import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Search, Menu, X, Sparkles, ChevronDown, UserRound } from "lucide-react";
import { TICKER_ITEMS } from "../data/mock";

const NAV = [
  {
    label: "Frameworks", to: "/frameworks",
    items: [
      { label: "Framework Library", to: "/frameworks" },
      { label: "ISO 31000", to: "/frameworks/iso-31000" },
      { label: "APRA CPS 234", to: "/frameworks/cps-234" },
      { label: "Essential Eight", to: "/frameworks/essential-eight" },
    ],
  },
  {
    label: "Australia Hub", to: "/australia",
    items: [
      { label: "All Regulators", to: "/australia" },
      { label: "APRA", to: "/australia/apra" },
      { label: "OAIC — Privacy", to: "/australia/oaic" },
      { label: "AUSTRAC", to: "/australia/austrac" },
      { label: "SOCI / Home Affairs", to: "/australia/home-affairs" },
    ],
  },
  { label: "Money Flow", to: "/money-flow", items: [
      { label: "AUD Intelligence Hub", to: "/money-flow" },
      { label: "RBA Cash Rate", to: "/money-flow" },
      { label: "NPP · PayID Corridors", to: "/money-flow" },
  ] },
  { label: "Tools", to: "/tools", items: [
      { label: "All Tools", to: "/tools" },
      { label: "CARS Calculator", to: "/tools/cars-calculator" },
  ] },
  { label: "Resources", to: "/glossary", items: [
      { label: "Glossary", to: "/glossary" },
      { label: "Insights", to: "/insights" },
  ] },
  { label: "About", to: "/about", items: [
      { label: "Our Story & Team", to: "/about" },
      { label: "Contact", to: "/contact" },
  ] },
];

export const Logo = ({ dark }) => (
  <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
    <span className={`font-heading text-[22px] font-bold tracking-tight relative ${dark ? "text-white" : "text-ink"}`}>
      RiskPedia
      <span className="absolute -top-0.5 -right-2 w-1.5 h-1.5 rounded-full bg-accent" />
    </span>
    <span className="font-mono text-[10px] font-semibold tracking-widest bg-accent text-white px-1.5 py-0.5 rounded-sm ml-1">AU</span>
  </Link>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [location.pathname]);

  return (
    <header className={`fixed top-0 inset-x-0 z-50 bg-white/95 backdrop-blur-md border-b transition-shadow duration-300 ${scrolled ? "shadow-[0_2px_20px_rgba(15,27,45,0.08)] border-neutral-200" : "border-neutral-100"}`}>
      <div className="h-[3px] bg-accent w-full" />
      <div className="max-w-[1320px] mx-auto px-5 lg:px-8 flex items-center h-16 gap-6">
        <Logo />
        <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
          {NAV.map((item) => (
            <div key={item.label} className="relative group">
              <NavLink
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) => `flex items-center gap-1 px-3 py-2 text-[13.5px] font-medium rounded-md transition-colors duration-200 ${isActive ? "text-accent" : "text-ink/80 hover:text-ink hover:bg-neutral-100"}`}
              >
                {item.label}
                <ChevronDown size={13} className="opacity-50 group-hover:rotate-180 transition-transform duration-200" />
              </NavLink>
              <div className="absolute left-0 top-full pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible translate-y-1 group-hover:translate-y-0 transition-[opacity,transform,visibility] duration-200 z-50">
                <div className="bg-white border border-neutral-200 rounded-lg shadow-[0_12px_40px_rgba(15,27,45,0.12)] py-2 min-w-[220px]">
                  {item.items.map((sub) => (
                    <Link key={sub.label} to={sub.to} className="block px-4 py-2 text-[13px] text-ink/70 hover:text-accent hover:bg-neutral-50 transition-colors duration-150">
                      {sub.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-2">
          <button aria-label="Search" className="p-2 text-ink/60 hover:text-ink rounded-md hover:bg-neutral-100 transition-colors duration-200">
            <Search size={17} />
          </button>
          <Link to="/contact" className="flex items-center gap-1.5 border border-neutral-300 rounded-md px-3 py-1.5 text-[13px] font-medium text-ink hover:border-ink transition-colors duration-200" data-testid="header-signin">
            <UserRound size={14} /> Sign in
          </Link>
          <Link to="/advisor" data-testid="header-cta" className="flex items-center gap-1.5 bg-accent text-white px-4 py-2 rounded-md text-[13px] font-semibold hover:bg-accent-dark transition-colors duration-200">
            <Sparkles size={14} /> Ask AI Advisor
          </Link>
        </div>
        <button className="lg:hidden ml-auto p-2 text-ink" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="mobile-menu-btn">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden bg-white border-t border-neutral-200 max-h-[calc(100vh-64px)] overflow-y-auto">
          {NAV.map((item) => (
            <div key={item.label} className="border-b border-neutral-100">
              <Link to={item.to} className="block px-6 py-3.5 font-heading font-semibold text-ink">{item.label}</Link>
              <div className="pb-2">
                {item.items.map((sub) => (
                  <Link key={sub.label} to={sub.to} className="block px-8 py-2 text-[13.5px] text-ink/60">{sub.label}</Link>
                ))}
              </div>
            </div>
          ))}
          <div className="p-5">
            <Link to="/advisor" className="flex items-center justify-center gap-2 bg-accent text-white px-4 py-3 rounded-md text-sm font-semibold w-full">
              <Sparkles size={15} /> Ask AI Advisor
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export const Ticker = () => (
  <div className="bg-ink text-white overflow-hidden py-2.5 border-y border-white/10" data-testid="regulatory-ticker">
    <div className="ticker-track flex whitespace-nowrap">
      {[...TICKER_ITEMS, ...TICKER_ITEMS].map((t, i) => (
        <span key={i} className="font-mono text-[11.5px] tracking-wide text-white/80 px-8 flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-accent inline-block" /> {t}
        </span>
      ))}
    </div>
  </div>
);

export const Eyebrow = ({ children, light }) => (
  <div className={`font-mono text-[11px] font-semibold tracking-[0.25em] uppercase mb-4 flex items-center gap-2 ${light ? "text-accent-light" : "text-accent"}`}>
    <span className="w-2 h-2 rounded-full border-2 border-current inline-block" />
    {children}
  </div>
);

export const Footer = () => (
  <footer className="bg-ink text-white mt-0">
    <div className="max-w-[1320px] mx-auto px-5 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
      <div className="lg:col-span-2 pr-8">
        <Logo dark />
        <p className="text-white/55 text-[13.5px] leading-relaxed mt-4 max-w-sm">
          The Risk Encyclopedia for Australia — APRA, OAIC, AUSTRAC, ACSC and SOCI translated into operating playbooks for CROs, internal auditors and GRC consultants across Australian BFSI.
        </p>
        <p className="font-mono text-[11px] text-white/35 mt-6 tracking-wide">Sydney · Melbourne · Brisbane — AEST</p>
      </div>
      {[
        { h: "Frameworks", links: [["ISO 31000", "/frameworks/iso-31000"], ["CPS 234", "/frameworks/cps-234"], ["CPS 230", "/frameworks/cps-230"], ["Essential Eight", "/frameworks/essential-eight"], ["AASB S2", "/frameworks/aasb-s2"]] },
        { h: "Australia", links: [["APRA", "/australia/apra"], ["ASIC", "/australia/asic"], ["OAIC", "/australia/oaic"], ["AUSTRAC", "/australia/austrac"], ["SOCI / CISC", "/australia/home-affairs"]] },
        { h: "Company", links: [["About", "/about"], ["Insights", "/insights"], ["AI Advisor", "/advisor"], ["Contact", "/contact"], ["Admin", "/admin"]] },
      ].map((col) => (
        <div key={col.h}>
          <h4 className="font-mono text-[11px] font-semibold tracking-[0.25em] uppercase text-white/45 mb-5">{col.h}</h4>
          <ul className="space-y-2.5">
            {col.links.map(([label, to]) => (
              <li key={label}>
                <Link to={to} className="text-[13.5px] text-white/70 hover:text-white transition-colors duration-150">{label}</Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
    <div className="border-t border-white/10">
      <div className="max-w-[1320px] mx-auto px-5 lg:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
        <p className="font-mono text-[11px] text-white/35 tracking-wide">© 2025 RiskPedia Australia. General information only — not financial or legal advice.</p>
        <p className="font-mono text-[11px] text-white/35 tracking-wide">ABN 00 000 000 000 · Made for AU BFSI</p>
      </div>
    </div>
  </footer>
);

export const PageShell = ({ children }) => (
  <div className="min-h-screen bg-white">
    <Header />
    <main className="pt-[67px]">{children}</main>
    <Footer />
  </div>
);
